// @ts-nocheck
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import AffiliateShell from './components/AffiliateShell'
import {AFF, card} from './components/affiliateTheme'
import {getAffiliateCustomers, getAgentListings, importAgentAI, autocompleteAgents, prepareCampaigns, syncAgentPosts, setAgentSyncSettings} from './core/_requests'

const AffiliateAgentImportWrapper = () => {
  const [params] = useSearchParams()
  const [customers, setCustomers] = useState<any[]>([])
  const [customerId, setCustomerId] = useState(params.get('customer') || '')
  const [phone, setPhone] = useState(params.get('phone') || '')
  const [ac, setAc] = useState<any[]>([])
  const [acOpen, setAcOpen] = useState(false)
  const [contactPhone, setContactPhone] = useState(params.get('contact') || '')
  // License line appended to the bottom of every imported post (editable).
  // Pre-fills from ?license= (convert flow); otherwise starts with the
  // "רישיון תיווך: " prefix so the affiliate only adds the number (and posts
  // never show a bare number). Auto-fills the agent's number when one is picked.
  const LICENSE_PREFIX = 'רישיון תיווך: '
  const [licenseText, setLicenseText] = useState(params.get('license') || LICENSE_PREFIX)
  // When the user typed nothing but the prefix, treat the license as empty.
  const effectiveLicense = () => {
    const t = licenseText.trim()
    return t && t !== LICENSE_PREFIX.trim() ? t : ''
  }
  // Agent name + agency shown in every imported post (editable, auto-filled
  // from the picked agent / convert flow).
  const [agentName, setAgentName] = useState(params.get('agent') || '')
  const [agencyName, setAgencyName] = useState(params.get('agency') || '')
  // Import with prices by default; affiliate can turn it off (?prices=0 from convert).
  const [includePrice, setIncludePrice] = useState(params.get('prices') !== '0')
  const [autoDaily, setAutoDaily] = useState(true) // enable daily auto-sync after import
  const [syncing, setSyncing] = useState(false)
  const [listings, setListings] = useState<any[]>([])
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [campaignName, setCampaignName] = useState('')
  const [useAI, setUseAI] = useState(true)
  const [includeNoImages, setIncludeNoImages] = useState(params.get('noimg') !== '0')
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState<{done: number; total: number} | null>(null)

  useEffect(() => {
    getAffiliateCustomers().then((r) => setCustomers(r.data || [])).catch(() => {})
    // Contact phone is intentionally NOT pre-filled — the client's number is often
    // the yad2 virtual number; the affiliate must enter a real WhatsApp number.
  }, [])

  useEffect(() => {
    if (params.get('phone')) fetchListings(params.get('phone'))
    // eslint-disable-next-line
  }, [])

  const fetchListings = async (p) => {
    const ph = (p ?? phone).trim()
    if (!ph) return
    setLoading(true)
    try {
      const res = await getAgentListings(ph)
      const rows = res.data?.results || []
      setListings(rows)
      const all = {}
      rows.forEach((r) => (all[r.token] = true))
      setSelected(all) // default select all
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  const onPhoneInput = async (v) => {
    setPhone(v)
    if (v && v.trim().length >= 2) {
      try {
        const r = await autocompleteAgents(v.trim())
        setAc(r.data?.results || [])
        setAcOpen(true)
      } catch (_) {}
    } else {
      setAc([])
      setAcOpen(false)
    }
  }
  const pickAgent = (a) => {
    setPhone(a.phone)
    setAcOpen(false)
    // Auto-fill the license line with the agent's number (unless the affiliate
    // already typed a custom one beyond the bare prefix).
    if (a.license_number && (!licenseText.trim() || licenseText.trim() === LICENSE_PREFIX.trim())) {
      setLicenseText(`${LICENSE_PREFIX}${a.license_number}`)
    }
    // Auto-fill agent name + agency from the picked agent (unless edited).
    if (a.name && !agentName.trim()) setAgentName(a.name)
    if (a.agency_name && !agencyName.trim()) setAgencyName(a.agency_name)
    fetchListings(a.phone)
  }

  const toggle = (token) => setSelected((s) => ({...s, [token]: !s[token]}))
  const selectedTokens = Object.keys(selected).filter((t) => selected[t])

  // Search by property name/address — agents can have 100+ listings.
  const [searchQ, setSearchQ] = useState('')
  const shownListings = useMemo(() => {
    const q = searchQ.trim().toLowerCase()
    if (!q) return listings
    return listings.filter((r) => {
      const hay = [r.name, r.property_type, r.street, r.neighborhood, r.city, r.post_content]
        .filter(Boolean).join(' ').toLowerCase()
      return hay.includes(q)
    })
  }, [listings, searchQ])

  // Select / deselect only the listings currently shown by the search.
  const setShownSelected = (value: boolean) => {
    setSelected((s) => {
      const next = {...s}
      shownListings.forEach((r) => (next[r.token] = value))
      return next
    })
  }

  // Run async tasks with bounded concurrency.
  const runPool = async (items: any[], worker: (x: any) => Promise<void>, concurrency: number) => {
    let idx = 0
    const runners = Array.from({length: Math.min(concurrency, items.length)}, async () => {
      while (idx < items.length) {
        const i = idx++
        await worker(items[i])
      }
    })
    await Promise.all(runners)
  }

  const doImport = async () => {
    if (!customerId) return alert('בחר חשבון לקוח')
    if (!contactPhone.trim()) return alert('יש להזין מספר וואטסאפ ליצירת קשר לפני ייבוא')
    // Warn (but allow) if the contact number is the yad2 agent number — likely virtual.
    const dig = (s) => String(s || '').replace(/\D/g, '')
    if (phone && dig(contactPhone).slice(-9) && dig(contactPhone).slice(-9) === dig(phone).slice(-9)) {
      if (!window.confirm('שים לב: המספר שהזנת זהה למספר מ‑יד2, שלרוב הוא מספר וירטואלי ולא וואטסאפ אמיתי. להמשיך בכל זאת?')) return
    }
    if (!selectedTokens.length) return alert('בחר לפחות נכס אחד')
    setImporting(true)
    setProgress({done: 0, total: selectedTokens.length})

    // City + commercial flag per token (from the loaded listings). Commercial
    // listings (משרדים/חנויות/…) go to one dedicated campaign, not per-city.
    const cityByToken: Record<string, string> = {}
    const commercialByToken: Record<string, boolean> = {}
    listings.forEach((l) => {
      cityByToken[l.token] = (l.city || '').trim() || 'כללי'
      commercialByToken[l.token] = l.category === 'commercial'
    })
    const COMMERCIAL_LABEL = 'נכסים מסחריים'
    const byCity: Record<string, string[]> = {}
    const commercialTokens: string[] = []
    selectedTokens.forEach((t) => {
      if (commercialByToken[t]) {
        commercialTokens.push(t)
        return
      }
      const c = cityByToken[t] || 'כללי'
      ;(byCity[c] = byCity[c] || []).push(t)
    })

    let imported = 0
    const byCampaign: Record<string, number> = {}
    try {
      // 1) Pre-create the per-city campaigns + the commercial campaign once (race-free).
      const prep = await prepareCampaigns(customerId, Object.keys(byCity), commercialTokens.length > 0)
      const cityId = prep.data?.campaigns || {}

      // 2) Build chunks (each tied to its campaign), then run them in parallel.
      const CHUNK = 5
      const chunks: {tokens: string[]; campaignId: string; city: string}[] = []
      Object.entries(byCity).forEach(([city, toks]) => {
        for (let i = 0; i < toks.length; i += CHUNK) {
          chunks.push({tokens: toks.slice(i, i + CHUNK), campaignId: cityId[city], city})
        }
      })
      const commercialId = cityId['__commercial__']
      if (commercialId && commercialTokens.length) {
        for (let i = 0; i < commercialTokens.length; i += CHUNK) {
          chunks.push({tokens: commercialTokens.slice(i, i + CHUNK), campaignId: commercialId, city: COMMERCIAL_LABEL})
        }
      }

      await runPool(
        chunks,
        async (chunk) => {
          const res = await importAgentAI({customerId, tokens: chunk.tokens, useAI, campaignId: chunk.campaignId, contactPhone: contactPhone || undefined, licenseText: effectiveLicense() || undefined, agentName: agentName.trim() || undefined, agencyName: agencyName.trim() || undefined, includePrice, includeNoImages})
          imported += res.data?.imported || 0
          byCampaign[chunk.city] = (byCampaign[chunk.city] || 0) + (res.data?.imported || 0)
          setProgress((p) => (p ? {done: Math.min(p.done + chunk.tokens.length, p.total), total: p.total} : p))
        },
        4 // up to 4 batches in flight at once
      )

      // Enable daily auto-sync going forward (so prices/new listings refresh daily).
      if (autoDaily && phone.trim()) {
        try {
          await setAgentSyncSettings({customerId, phone: phone.trim(), enabled: true, contactPhone: contactPhone || undefined, licenseText: effectiveLicense() || undefined, agentName: agentName.trim() || undefined, agencyName: agencyName.trim() || undefined, includePrice, useAI, includeNoImages})
        } catch (_) {}
      }
      const summary = Object.entries(byCampaign).map(([n, c]) => `• ${n}: ${c}`).join('\n')
      alert(`יובאו ${imported} פוסטים ל-${Object.keys(byCampaign).length} קמפיינים (לפי עיר):\n${summary}${autoDaily ? '\n\n🔄 סנכרון יומי אוטומטי הופעל — המחירים והנכסים יתעדכנו כל יום.' : ''}`)
      setSelected({})
    } catch (e: any) {
      alert(`הייבוא נעצר אחרי ${imported} פוסטים: ` + (e?.response?.data?.error || e?.message))
    }
    setImporting(false)
    setProgress(null)
  }

  // Sync now: refresh existing posts (price/details) + add new listings.
  const doSyncNow = async () => {
    if (!customerId) return alert('בחר חשבון לקוח')
    if (!phone.trim()) return alert('בחר סוכן (טלפון)')
    setSyncing(true)
    try {
      const res = await syncAgentPosts({customerId, phone: phone.trim(), contactPhone: contactPhone || undefined, licenseText: effectiveLicense() || undefined, agentName: agentName.trim() || undefined, agencyName: agencyName.trim() || undefined, includePrice, useAI, autoDaily, includeNoImages})
      if (res.data?.success) {
        alert(`סנכרון הושלם ✓\nעודכנו ${res.data.updated} פוסטים, נוספו ${res.data.added} חדשים, הוסרו ${(res.data as any).removed || 0} שנמכרו/ירדו.${res.data.auto_daily ? '\n🔄 סנכרון יומי פעיל.' : ''}`)
      } else {
        alert(res.data?.error || 'הסנכרון נכשל')
      }
    } catch (e: any) {
      alert('הסנכרון נכשל: ' + (e?.response?.data?.error || e?.message))
    }
    setSyncing(false)
  }

  // When arriving from the lead-convert flow (?autostart=1) with customer + contact + phone
  // already supplied, kick off the import automatically once the listings have loaded.
  const autostartedRef = useRef(false)
  useEffect(() => {
    if (
      params.get('autostart') === '1' &&
      !autostartedRef.current &&
      !importing &&
      listings.length > 0 &&
      customerId &&
      contactPhone.trim()
    ) {
      autostartedRef.current = true
      doImport()
    }
    // eslint-disable-next-line
  }, [listings, customerId, contactPhone])

  const inputStyle = {border: `1px solid ${AFF.border}`}

  return (
    <AffiliateShell
      title='ייבוא לפי סוכן (AI)'
      subtitle='הזן מספר טלפון של סוכן, משוך את כל הנכסים שלו, וצור קמפיין עם פוסטים שנכתבים ע"י AI.'
    >
      <div style={{...card, padding: 20, marginBottom: 20}}>
        <div className='row g-3 align-items-end'>
          <div className='col-md-6' style={{position: 'relative'}}>
            <label style={{fontWeight: 600, color: AFF.ink, marginBottom: 6, display: 'block'}}>סוכן (שם / טלפון)</label>
            <input
              className='form-control'
              style={inputStyle}
              value={phone}
              onChange={(e) => onPhoneInput(e.target.value)}
              onFocus={() => ac.length && setAcOpen(true)}
              onBlur={() => setTimeout(() => setAcOpen(false), 200)}
              placeholder='הקלד שם סוכן או טלפון…'
              autoComplete='off'
            />
            {acOpen && ac.length > 0 && (
              <div style={{position: 'absolute', zIndex: 20, top: '100%', insetInlineStart: 12, insetInlineEnd: 12, background: '#fff', border: `1px solid ${AFF.border}`, borderRadius: 12, boxShadow: AFF.cardShadow, maxHeight: 280, overflow: 'auto'}}>
                {ac.map((a) => (
                  <div
                    key={a.phone}
                    onMouseDown={() => pickAgent(a)}
                    style={{padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${AFF.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8}}
                  >
                    <div>
                      <div style={{fontWeight: 600, color: AFF.ink}}>{a.name || a.phone}</div>
                      <div style={{fontSize: 12, color: AFF.muted}} dir='ltr'>{a.phone}{a.agency_name ? ` · ${a.agency_name}` : ''}</div>
                    </div>
                    <span className='badge badge-light-info' style={{whiteSpace: 'nowrap'}}>{a.listings_count} נכסים</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className='col-md-2'>
            <button className='btn w-100' onClick={() => fetchListings()} disabled={loading} style={{background: AFF.accent, color: '#fff', fontWeight: 700}}>
              {loading ? '...' : 'משוך נכסים'}
            </button>
          </div>
          <div className='col-md-2'>
            <label style={{display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: AFF.ink, cursor: 'pointer', paddingTop: 8}}>
              <input type='checkbox' className='form-check-input' checked={useAI} onChange={(e) => setUseAI(e.target.checked)} />
              כתיבה ע"י AI
            </label>
          </div>
          <div className='col-md-2'>
            <label style={{display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: AFF.ink, cursor: 'pointer', paddingTop: 8}} title='כשמכובה — הפוסטים יובאו ללא שורת המחיר'>
              <input type='checkbox' className='form-check-input' checked={includePrice} onChange={(e) => setIncludePrice(e.target.checked)} />
              כלול מחירים
            </label>
          </div>
          <div className='col-md-2'>
            <label style={{display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: AFF.ink, cursor: 'pointer', paddingTop: 8}} title='אם מכובה — מודעות ללא תמונות ייובאו אך לא יפורסמו (יישארו מושבתות)'>
              <input type='checkbox' className='form-check-input' checked={includeNoImages} onChange={(e) => setIncludeNoImages(e.target.checked)} />
              כלול ללא תמונות
            </label>
          </div>
          {/* License line appended to the bottom of every imported post (editable) */}
          <div className='col-md-6'>
            <label style={{fontWeight: 600, color: AFF.ink, marginBottom: 6, display: 'block'}}>רישיון תיווך (לא חובה)</label>
            <input
              className='form-control'
              style={inputStyle}
              value={licenseText}
              onChange={(e) => setLicenseText(e.target.value)}
              placeholder='למשל: רישיון תיווך: 12345'
            />
            <div style={{fontSize: 11.5, color: AFF.muted, marginTop: 4}}>יתווסף בתחתית כל פוסט מיובא. הוסיפו את מספר הרישיון אחרי "רישיון תיווך:" (או מחקו אם אין).</div>
          </div>
          {/* Agent name + agency shown in every imported post (editable) */}
          <div className='col-md-3'>
            <label style={{fontWeight: 600, color: AFF.ink, marginBottom: 6, display: 'block'}}>שם הסוכן (לא חובה)</label>
            <input
              className='form-control'
              style={inputStyle}
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder='למשל: ישראל ישראלי'
            />
          </div>
          <div className='col-md-3'>
            <label style={{fontWeight: 600, color: AFF.ink, marginBottom: 6, display: 'block'}}>שם הסוכנות (לא חובה)</label>
            <input
              className='form-control'
              style={inputStyle}
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              placeholder='למשל: רי/מקס נדל"ן'
            />
          </div>
        </div>
      </div>

      {listings.length > 0 && (
        <div style={{...card, padding: '14px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, position: 'sticky', top: 10, zIndex: 5}}>
          <div style={{fontWeight: 600, color: AFF.ink, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
            <span>
              {searchQ ? `${shownListings.length}/${listings.length}` : listings.length} נכסים · נבחרו <span style={{color: AFF.accent}}>{selectedTokens.length}</span>
            </span>
            <div style={{position: 'relative'}}>
              <i className='bi bi-search' style={{position: 'absolute', insetInlineStart: 10, top: '50%', transform: 'translateY(-50%)', color: AFF.muted, fontSize: 12}} />
              <input
                className='form-control form-control-sm'
                style={{width: 220, paddingInlineStart: 30}}
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder='חפש לפי שם נכס / רחוב / שכונה…'
              />
              {searchQ && (
                <button
                  onClick={() => setSearchQ('')}
                  title='נקה'
                  style={{position: 'absolute', insetInlineEnd: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: AFF.muted, cursor: 'pointer', padding: 2}}
                >
                  <i className='bi bi-x-circle-fill' style={{fontSize: 12}} />
                </button>
              )}
            </div>
            {searchQ && shownListings.length > 0 && (
              <>
                <button className='btn btn-sm btn-light-primary' onClick={() => setShownSelected(true)}>בחר מוצגים</button>
                <button className='btn btn-sm btn-light' onClick={() => setShownSelected(false)}>בטל מוצגים</button>
              </>
            )}
          </div>
          <div style={{display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap'}}>
            <select
              className='form-select form-select-sm'
              style={{...inputStyle, width: 200}}
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <option value=''>בחר חשבון לקוח…</option>
              {customers.map((c) => (
                <option key={c._id} value={c._id}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email}</option>
              ))}
            </select>
            <input
              className='form-control form-control-sm'
              style={{...inputStyle, width: 150}}
              dir='ltr'
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder='וואטסאפ ליצירת קשר *'
              title='חובה — מופיע בכל פוסט עם קישור וואטסאפ. ברירת מחדל: טלפון הלקוח'
            />
            {licenseText.trim() && (
              <span style={{fontSize: 12, color: AFF.success, whiteSpace: 'nowrap'}} title={licenseText}>
                <i className='bi bi-patch-check-fill' style={{marginInlineEnd: 4}} />רישיון יתווסף
              </span>
            )}
            <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: AFF.ink, cursor: 'pointer', whiteSpace: 'nowrap'}} title='לעדכן מחירים ולהוסיף נכסים חדשים אוטומטית פעם ביום'>
              <input type='checkbox' className='form-check-input' checked={autoDaily} onChange={(e) => setAutoDaily(e.target.checked)} />
              <i className='bi bi-arrow-repeat' />סנכרון יומי
            </label>
            <button className='btn btn-sm' onClick={doImport} disabled={importing || !selectedTokens.length || !customerId || !contactPhone.trim()} style={{background: AFF.success, color: '#fff', fontWeight: 700}}>
              {importing ? 'מייבא…' : `ייבא ${selectedTokens.length} נכסים`}
            </button>
            <button className='btn btn-sm btn-light' onClick={doSyncNow} disabled={syncing || !customerId || !phone.trim()} title='עדכן את כל הפוסטים הקיימים והוסף נכסים חדשים מיד2 (לפי הטלפון של הסוכן)'>
              <i className='bi bi-arrow-repeat me-1' />{syncing ? 'מסנכרן…' : 'סנכרן עכשיו'}
            </button>
          </div>
        </div>
      )}

      {importing && progress && (
        <div style={{...card, padding: 20, marginBottom: 20}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, color: AFF.ink, fontWeight: 600}}>
            <span>
              <span className='spinner-border spinner-border-sm me-2' />
              מייבא — מוריד ומעלה תמונות ומריץ AI…
            </span>
            <span style={{color: AFF.accent}}>{progress.done} / {progress.total}</span>
          </div>
          <div style={{height: 10, background: AFF.border, borderRadius: 99, overflow: 'hidden'}}>
            <div style={{height: 10, width: `${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%`, background: AFF.accentGrad, transition: 'width .3s'}} />
          </div>
        </div>
      )}

      <div className='row g-4'>
        {shownListings.map((r) => {
          const isSel = !!selected[r.token]
          return (
            <div className='col-sm-6 col-lg-4 col-xl-3' key={r.token}>
              <div onClick={() => toggle(r.token)} style={{...card, overflow: 'hidden', cursor: 'pointer', height: '100%', border: `2px solid ${isSel ? AFF.accent : AFF.border}`, position: 'relative'}}>
                {isSel && (
                  <div style={{position: 'absolute', top: 10, insetInlineEnd: 10, width: 26, height: 26, borderRadius: '50%', background: AFF.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2}}>
                    <i className='bi bi-check2' />
                  </div>
                )}
                <div style={{height: 150, background: AFF.border, backgroundImage: r.cover_image ? `url(${r.cover_image})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center'}} />
                <div style={{padding: 14}}>
                  {/* Listing name = type + street/neighborhood + city (same name the post gets on import) */}
                  <div style={{fontWeight: 700, color: AFF.ink}}>{r.name || r.property_type || 'נכס'}</div>
                  <div style={{fontSize: 12.5, color: AFF.muted, marginTop: 4, whiteSpace: 'pre-line', maxHeight: 72, overflow: 'hidden'}}>{r.post_content}</div>
                  {r.yad2_url && (
                    <a
                      href={r.yad2_url}
                      target='_blank'
                      rel='noreferrer'
                      onClick={(e) => e.stopPropagation()}
                      style={{display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: 12, fontWeight: 600, color: AFF.accent}}
                    >
                      <i className='bi bi-box-arrow-up-left' /> אימות ביד2
                    </a>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {listings.length > 0 && shownListings.length === 0 && (
        <div style={{...card, padding: 40, textAlign: 'center', color: AFF.muted}}>
          <i className='bi bi-search' style={{fontSize: 30, opacity: 0.4, display: 'block', marginBottom: 8}} />
          לא נמצאו נכסים לחיפוש "{searchQ}"
        </div>
      )}

      {listings.length === 0 && !loading && (
        <div style={{...card, padding: 60, textAlign: 'center', color: AFF.muted}}>
          <i className='bi bi-robot' style={{fontSize: 40, opacity: 0.4, display: 'block', marginBottom: 10}} />
          הזן טלפון של סוכן ולחץ "משוך נכסים".
        </div>
      )}
    </AffiliateShell>
  )
}

export default AffiliateAgentImportWrapper
